import { useEffect } from 'react';

import { json, type LoaderFunctionArgs } from '@vercel/remix';
import BeforeAfter from '~/components/LandingPage/BeforeAfter';
import CallToAction from '~/components/LandingPage/CallToAction';
import Faq from '~/components/LandingPage/Faq';
import Feature1 from '~/components/LandingPage/Feature1';
import Feature2 from '~/components/LandingPage/Feature2';
import Footer from '~/components/LandingPage/Footer';
import Hero from '~/components/LandingPage/Hero';
import HowItWorks from '~/components/LandingPage/HowItWorks';
import KeyBenefits from '~/components/LandingPage/KeyBenefits';
import KeyBenefitsTwo from '~/components/LandingPage/KeyBenefitsTwo';
import Partners from '~/components/LandingPage/Partners';
import Pricing from '~/components/LandingPage/Pricing';
import VideoCarousel from '~/components/LandingPage/VideoCarousel';
// import { useVitePostHog } from 'vite-plugin-posthog/react';
// import { getSupabase } from '~/services/supabase.server';
import { createCampaignDataCookie } from '~/utils/cookies.server';

export const meta = () => [
	{
		title: 'Create Shopify AI Product Pages & Stores in 2 Minutes | PagePilot.ai',
	},
	{
		charset: 'utf-8',
	},
	{
		name: 'viewport',
		content: 'width=device-width, initial-scale=1.0',
	},
	{
		name: 'impact-site-verification',
		content: 'ea7c9cbe-eb25-42d1-96ad-dd219d150bf7',
	},
	{
		name: 'msapplication-TileColor',
		content: '#fff',
	},
	{
		name: 'theme-color',
		content: '#ffffff',
	},
	{
		name: 'keywords',
		content:
			'product description generator,shopify landing page generator,shopify ai description,shopify product page generator,ai product page generator,instant landing page creation,high-quality landing page builder,product description for shopify,easy-to-use landing page generator,quick product page creation,automated product description tool,dropshipping copy generator,fast landing page builder for dropshipping,free product description generator,ai product description,shopify description maker,text generator for product descriptions,automated copywriting for dropshipping,product copy generator,landing page generator for shopify,dropshipping copyright,product page copy generator for shopify,shopify product description tool,user-friendly product copy generator,high-converting landing page builder,copywriting tool for online shops,easy-to-use product description generator,shopify dropshipping copy generator,landing page creation tool for online shops,product page generator for shopify stores,ai-powered product description generator,optimized copy and images for dropshipping,shopify landing page builder,user-oriented copywriting tool,e-commerce copy generator,dynamic product description tool,conversion-focused landing page builder,ai-driven copywriting for dropshipping,product page optimization for shopify,customizable landing page templates,dropshipping copy and image optimization,dropshipping copyright tool,shopify copyright ai,product copyright shopify,shopify store copy generator,streamlined product description creation,automated copywriting for online shops,enhanced landing page performance,seo-friendly product page generator,persuasive copywriting for dropshippers,shopify landing page optimization,user-centric product copy generator,effortless creation of product descriptions,targeted copywriting for e-commerce,seamless integration with shopify,conversion-optimized landing page templates,comprehensive copy and image solution for dropshippers,easy shopify listing tool,shopify product list ai,dropshipping product listing tool,free dropshipping product listing,list shopify product fast,import shopify products,shopify product description maker,easy product description maker,product description writer,easy product listing,shopify product generator,ai shopify description',
	},
	{
		name: 'description',
		content:
			'Create high converting product descriptions and landing pages effortlessly with PagePilot.ai - the ultimate free AI tool for easy product listing. Boost your conversions with high converting copy. Listing products on Shopify easy with the power of AI. Powered by AI for Shopify Dropshipping Community.',
	},
	{
		name: 'og:title',
		content: 'PagePilot.ai - Find your winning product 20X faster using AI',
	},
	{
		name: 'og:description',
		content:
			'Create high converting product descriptions and landing pages effortlessly with PagePilot.ai - the ultimate free AI tool for easy product listing. Boost your conversions with high converting copy. Listing products on Shopify easy with the power of AI. Powered by AI for Shopify Dropshipping Community.',
	},
	{
		name: 'og:image',
		content: 'https://pagepilot.ai/images/metaImage.jpg',
	},
	{
		name: 'og:url',
		content: 'https://pagepilot.ai',
	},
];

export const headers = () => {
	return {
		'Cache-Control': 'public, max-age=31536000',
	};
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
	// Get campaign cookie
	const { getSupabase } = await import('~/services/supabase.server');
	const cookie = request.headers.get('Cookie');
	const campaignCookie = (await createCampaignDataCookie.parse(cookie)) || {};

	// Get url params
	const urlParams = new URLSearchParams(request.url.split('?')[1]);
	const params = Object.fromEntries(urlParams.entries());

	// If there are url params, add them to the campaign cookie
	if (request.url.split('?')[1]) {
		campaignCookie.campaignData = params;
	}

	// Get session
	const { supabase, headers } = getSupabase(request);
	const {
		data: { session },
	} = await supabase.auth.getSession();

	return json(
		{
			userIsLoggedIn: !!session,
		},
		{
			headers: {
				...headers,
				'Set-Cookie': await createCampaignDataCookie.serialize(campaignCookie),
				'Cache-Control': 'public, max-age=31536000',
			},
		},
	);
};

const HomePage = () => {
	// const posthog = useVitePostHog();

	// const [posthogFlag, setPosthogFlag] = useState<string | null>(null);

	// useEffect(() => {
	// 	console.log('flagg >>>>', posthog?.getFeatureFlag('landingpage-alen'));
	// 	try {
	// 		if (posthog?.getFeatureFlag('landingpage-alen') === 'alen') {
	// 			setPosthogFlag('alen');
	// 		} else {
	// 			setPosthogFlag('control');
	// 		}
	// 	} catch (error) {
	// 		setPosthogFlag('errorflag');
	// 	}
	// }, [posthog]);

	useEffect(() => {
		const klaviyoFreeTrialModal = localStorage.getItem('klaviyoFreeTrialModal-Tkz5At');

		if (!klaviyoFreeTrialModal) {
			const timer = setTimeout(() => {
				if (window && window._klOnsite) {
					window._klOnsite = window._klOnsite || [];
					window._klOnsite.push(['openForm', 'Tkz5At']);
					localStorage.setItem('klaviyoFreeTrialModal-Tkz5At', 'true');
				}
			}, 20000);

			return () => clearTimeout(timer);
		}
	}, []);

	return (
		<div className="text-gray font-dm antialiased">
			<div className="flex min-h-screen flex-col overflow-hidden">
				<main className="grow">
					<div className="bg-[#050520]">
						<Hero />
						<KeyBenefits />
						<Partners />
						<Feature1 />
						<HowItWorks />
						<Feature2 />
						<KeyBenefitsTwo />
						<BeforeAfter />
						<VideoCarousel />
						<Pricing />
						<Faq />
						<CallToAction />
						<Footer />
					</div>
				</main>
			</div>
		</div>
	);
};

export default HomePage;
